export type LinhaMensagem = {
  usuario_responsavel_id: number | null;
  usuario_responsavel_nome: string | null;
  qtd_chats: number;
  avg_analise_sentimento_cliente: number | null;
  avg_analise_comportamento_atendente: number | null;
  avg_tempo_resposta_segundos: number | null;
};

export type LinhaScore = {
  usuario_responsavel_id: number | null;
  usuario_responsavel_nome: string | null;
  avg_score: number | null;
};

export type LinhaAtendente = {
  id: number;
  nome: string;
  qtdChats: number;
  nps: number | null;
  sentimento: number | null;
  comportamento: number | null;
  tempoRespostaSegundos: number | null;
  tempoRespostaScore: number | null;
  /** Variação vs. período anterior; só existe depois de `anexarDelta` */
  delta?: Deltas;
};

export type Coluna = 'nome' | 'qtdChats' | 'nps' | 'sentimento' | 'comportamento' | 'tempoRespostaScore';
export type Direcao = 'asc' | 'desc';

// Acima disso o tempo de resposta é considerado o pior caso (0%, vermelho).
// Sem um teto não dá pra converter segundos numa escala comparável às outras métricas (0-100%).
const TETO_TEMPO_RESPOSTA_SEGUNDOS = 10 * 60;

export const formatTempo = (segundos: number): string => {
  const m = Math.floor(segundos / 60);
  const s = Math.round(segundos % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const scoreTempoResposta = (segundos: number | null): number | null =>
  segundos == null ? null : Math.max(0, 100 - (segundos / TETO_TEMPO_RESPOSTA_SEGUNDOS) * 100);

function linhaVazia(id: number, nome: string | null): LinhaAtendente {
  return {
    id,
    nome: nome ?? `#${id}`,
    qtdChats: 0,
    nps: null,
    sentimento: null,
    comportamento: null,
    tempoRespostaSegundos: null,
    tempoRespostaScore: null,
  };
}

/** Junta as duas fontes (mensagens e pontuação) numa linha por atendente, por `usuario_responsavel_id`. */
export function mesclarLinhas(mensagens: LinhaMensagem[], scores: LinhaScore[]): LinhaAtendente[] {
  const porId = new Map<number, LinhaAtendente>();

  for (const row of mensagens) {
    if (row.usuario_responsavel_id == null) continue;
    const linha = linhaVazia(row.usuario_responsavel_id, row.usuario_responsavel_nome);
    linha.qtdChats = row.qtd_chats;
    linha.sentimento = row.avg_analise_sentimento_cliente != null ? row.avg_analise_sentimento_cliente * 100 : null;
    linha.comportamento = row.avg_analise_comportamento_atendente != null ? row.avg_analise_comportamento_atendente * 100 : null;
    linha.tempoRespostaSegundos = row.avg_tempo_resposta_segundos;
    linha.tempoRespostaScore = scoreTempoResposta(row.avg_tempo_resposta_segundos);
    porId.set(linha.id, linha);
  }

  for (const row of scores) {
    if (row.usuario_responsavel_id == null) continue;
    const linha = porId.get(row.usuario_responsavel_id) ?? linhaVazia(row.usuario_responsavel_id, row.usuario_responsavel_nome);
    linha.nps = row.avg_score;
    porId.set(linha.id, linha);
  }

  return [...porId.values()];
}

/** Compara dois valores pra ordenação de coluna: nulo sempre por último, na direção pedida pros demais. */
export const comparar = (va: string | number | null, vb: string | number | null, dir: Direcao): number => {
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  const cmp = typeof va === 'string' || typeof vb === 'string' ? String(va).localeCompare(String(vb)) : (va as number) - (vb as number);
  return dir === 'asc' ? cmp : -cmp;
};

export function ordenarLinhas(linhas: LinhaAtendente[], sortBy: Coluna, sortDir: Direcao): LinhaAtendente[] {
  return [...linhas].sort((a, b) => comparar(a[sortBy], b[sortBy], sortDir));
}

// ---- Comparação com o período anterior ----

// Médias sobre poucos atendimentos oscilam demais: um único chat ruim move a média
// de 3 atendimentos em dezenas de pontos, e a seta viraria ruído em vez de sinal.
// Não se aplica à contagem de atendimentos, que é exata em qualquer volume.
export const MIN_CHATS_PARA_DELTA = 5;

export type Deltas = {
  /** Variação relativa da quantidade de atendimentos, em % */
  qtdChatsPercentual: number | null;
  /** Variações em pontos percentuais (as métricas já são percentuais de 0 a 100) */
  nps: number | null;
  sentimento: number | null;
  comportamento: number | null;
  /** Variação em segundos; negativo = respondendo mais rápido */
  tempoRespostaSegundos: number | null;
};

const DELTAS_VAZIOS: Deltas = {
  qtdChatsPercentual: null,
  nps: null,
  sentimento: null,
  comportamento: null,
  tempoRespostaSegundos: null,
};

const diferenca = (atual: number | null, anterior: number | null): number | null =>
  atual == null || anterior == null ? null : atual - anterior;

/**
 * Anexa a cada linha a variação em relação à mesma linha do período anterior.
 *
 * Sem linha correspondente no período anterior (atendente novo, ou que não atendeu
 * naquela janela) não há base de comparação: o delta fica nulo em vez de virar +100%.
 */
export function anexarDelta(
  atuais: LinhaAtendente[],
  anteriores: LinhaAtendente[],
): LinhaAtendente[] {
  const anteriorPorId = new Map(anteriores.map((l) => [l.id, l]));

  return atuais.map((linha) => {
    const anterior = anteriorPorId.get(linha.id);
    if (!anterior) return { ...linha, delta: DELTAS_VAZIOS };

    const qtdChatsPercentual =
      anterior.qtdChats > 0 ? ((linha.qtdChats - anterior.qtdChats) / anterior.qtdChats) * 100 : null;

    const amostraSuficiente =
      linha.qtdChats >= MIN_CHATS_PARA_DELTA && anterior.qtdChats >= MIN_CHATS_PARA_DELTA;

    return {
      ...linha,
      delta: {
        qtdChatsPercentual,
        nps: amostraSuficiente ? diferenca(linha.nps, anterior.nps) : null,
        sentimento: amostraSuficiente ? diferenca(linha.sentimento, anterior.sentimento) : null,
        comportamento: amostraSuficiente ? diferenca(linha.comportamento, anterior.comportamento) : null,
        tempoRespostaSegundos: amostraSuficiente
          ? diferenca(linha.tempoRespostaSegundos, anterior.tempoRespostaSegundos)
          : null,
      },
    };
  });
}

export const formatDeltaPontos = (delta: number): string => `${Math.round(delta)} p.p.`;
export const formatDeltaPercentual = (delta: number): string => `${Math.round(delta)}%`;
export const formatDeltaTempo = (delta: number): string => formatTempo(delta);
